import { Controller } from 'stimulus';

/**
 * Inline editing of billing sheet values.
 *
 * Supports two cell types:
 *   1. Override cells (data-override-field): saves to the override JSONB column
 *   2. Direct field cells (data-direct-field): saves directly to the sheet column
 *
 * @value url - the PATCH endpoint for override saves
 * @value fieldUrl - the PATCH endpoint for direct field saves
 * @value sheetId - the MonthlyBillingSheet ID
 * @value locked - whether the sheet is invoice-locked
 */
export default class extends Controller {
  static values = {
    url: String,
    fieldUrl: String,
    sheetId: Number,
    locked: Boolean,
  };

  edit(event) {
    const cell = this.findCell(event);
    if (!cell) return;

    if (this.lockedValue && cell.dataset.directField !== 'invoice_sent') return;

    cell.querySelector('[data-role="display"]').classList.add('d-none');
    cell.querySelector('[data-role="edit"]').classList.remove('d-none');

    const input = cell.querySelector('input, textarea, select');
    input.focus();
    if (input.tagName !== 'SELECT' && input.select) input.select();
  }

  cancel(event) {
    const cell = this.findCell(event);
    if (!cell) return;

    const input = cell.querySelector('input, textarea, select');
    input.value = input.dataset.currentValue || '';

    cell.querySelector('[data-role="edit"]').classList.add('d-none');
    cell.querySelector('[data-role="display"]').classList.remove('d-none');
  }

  async save(event) {
    const cell = this.findCell(event);
    if (!cell) return;

    if (cell.dataset.directField) {
      await this.saveDirectField(cell);
    } else {
      await this.saveOverride(cell);
    }
  }

  keydown(event) {
    if (event.key === 'Escape') {
      this.cancel(event);
    }
    // Enter saves for inputs but not textareas (where Enter adds a newline)
    if (event.key === 'Enter' && event.target.tagName !== 'TEXTAREA') {
      event.preventDefault();
      this.save(event);
    }
  }

  // --- private ---

  findCell(event) {
    return event.target.closest('[data-override-field], [data-direct-field]');
  }

  async saveOverride(cell) {
    const field = cell.dataset.overrideField;
    const dataType = cell.dataset.overrideType;
    const input = cell.querySelector('input');
    const newValue = input.value.trim();

    const body = {
      sheet_id: this.sheetIdValue,
      data_type: dataType,
      field: field,
      value: newValue === '' ? null : newValue,
    };

    try {
      const response = await fetch(this.urlValue, {
        method: 'PATCH',
        headers: this.requestHeaders(),
        body: JSON.stringify(body),
      });

      if (response.ok) {
        await response.json();
        const displayValue =
          newValue === '' ? cell.dataset.autoValue || '-' : newValue;
        const displaySpan = cell.querySelector('[data-role="display-value"]');
        displaySpan.textContent = displayValue;

        if (newValue !== '' && newValue !== cell.dataset.autoValue) {
          displaySpan.classList.add('fw-bold');
        } else {
          displaySpan.classList.remove('fw-bold');
        }

        input.dataset.currentValue = newValue;
        this.closeEdit(cell);
        this.flashSaved(cell);
      } else {
        const data = await response.json();
        console.error('Override save failed:', data.errors);
      }
    } catch (error) {
      console.error('Override save error:', error);
    }
  }

  async saveDirectField(cell) {
    const field = cell.dataset.directField;
    const input = cell.querySelector('input, textarea, select');
    const newValue = input.value.trim();

    const body = {
      sheet_id: this.sheetIdValue,
      field: field,
      value: newValue,
    };

    try {
      const response = await fetch(this.fieldUrlValue, {
        method: 'PATCH',
        headers: this.requestHeaders(),
        body: JSON.stringify(body),
      });

      if (response.ok) {
        const data = await response.json();

        const displaySpan = cell.querySelector('[data-role="display-value"]');
        if (input.tagName === 'SELECT' && input.selectedOptions.length) {
          displaySpan.textContent = input.selectedOptions[0].text;
        } else {
          displaySpan.textContent = newValue || '-';
        }

        input.dataset.currentValue = newValue;
        this.closeEdit(cell);
        this.flashSaved(cell);

        if ('locked' in data) {
          this.lockedValue = data.locked;
          if (this.lockedValue) this.cancelAllEdits(cell);
          this.toggleEditIcons(cell);
        }
      } else {
        const data = await response.json();
        console.error('Field save failed:', data.errors);
      }
    } catch (error) {
      console.error('Field save error:', error);
    }
  }

  closeEdit(cell) {
    cell.querySelector('[data-role="edit"]').classList.add('d-none');
    cell.querySelector('[data-role="display"]').classList.remove('d-none');
  }

  requestHeaders() {
    return {
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')
        ?.content,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
  }

  toggleEditIcons(excludeCell) {
    const icons = this.element.querySelectorAll('.amba-billing-edit-icon');
    icons.forEach((icon) => {
      if (excludeCell && excludeCell.contains(icon)) return;
      icon.classList.toggle('d-none', this.lockedValue);
    });

    const lockables = this.element.querySelectorAll('[data-billing-lockable]');
    lockables.forEach((el) => {
      el.classList.toggle('d-none', this.lockedValue);
    });
  }

  cancelAllEdits(excludeCell) {
    const openCells = this.element.querySelectorAll(
      '[data-override-field], [data-direct-field]'
    );
    openCells.forEach((cell) => {
      if (cell === excludeCell) return;
      const editEl = cell.querySelector('[data-role="edit"]');
      if (!editEl || editEl.classList.contains('d-none')) return;

      const input = cell.querySelector('input, textarea, select');
      if (input) input.value = input.dataset.currentValue || '';
      this.closeEdit(cell);
    });
  }

  flashSaved(cell) {
    cell.classList.add('amba-billing-cell-saved');
    setTimeout(() => cell.classList.remove('amba-billing-cell-saved'), 1500);
  }
}
